import { Drawer } from '@mantine/core'
import { useRef, useEffect, useState } from 'react'
import AOS from 'aos'

const PreNoticeDrawer = ({
  opened,
  onClose,
  height,
  children,
  id,
}: {
  opened: boolean
  onClose: () => void
  height?: string
  children: React.ReactNode
  id?: string
}) => {
  const handleRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [drawerId] = useState(id || `drawer-${Math.random().toString(36).substr(2, 9)}`)

  // 현재 Drawer의 DOM 요소 참조
  const drawerElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!opened) return

    // Drawer가 열렸을 때만 처리
    const timeoutId = setTimeout(() => {
      if (contentRef.current) {
        // AOS 새로고침
        AOS.refresh()

        // 컨텐츠 영역 스크롤 이벤트 핸들러
        const handleScroll = () => {
          AOS.refresh()
        }

        // 스크롤 이벤트 리스너 등록
        contentRef.current.addEventListener('scroll', handleScroll)

        return () => {
          if (contentRef.current) {
            contentRef.current.removeEventListener('scroll', handleScroll)
          }
        }
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [opened])

  // 드래그 기능 구현
  useEffect(() => {
    if (!opened) return

    const timeoutId = setTimeout(() => {
      // 드래그 핸들러 요소를 통해 정확한 Drawer 찾기
      if (!handleRef.current) return

      // 핸들에 고유 속성 추가
      handleRef.current.setAttribute('data-drawer-id', drawerId)

      // 핸들 요소가 속한 Drawer Content 요소 찾기
      const drawerContent = handleRef.current.closest('.mantine-Drawer-content') as HTMLElement
      if (!drawerContent) return

      // 찾은 Drawer 요소 저장
      drawerElementRef.current = drawerContent

      // 드래그 요소에 ID 표시 (디버깅용)
      console.log(`Drawer ${drawerId} 드래그 설정 완료`, drawerContent)

      let startY = 0
      let currentY = 0
      let isDragging = false
      let originalTransform = ''

      // 드래그 시작
      const handleDragStart = (clientY: number) => {
        isDragging = true
        startY = clientY
        currentY = 0

        // 원래 transform 값 저장
        originalTransform = drawerContent.style.transform

        // 애니메이션 일시 제거
        drawerContent.style.transition = 'none'

        console.log(`Drawer ${drawerId} 드래그 시작`)
      }

      // 드래그 중
      const handleDrag = (clientY: number) => {
        if (!isDragging) return

        // 아래로만 드래그 가능
        const delta = Math.max(0, clientY - startY)
        currentY = delta

        // 기존 transform에 translateY 추가
        drawerContent.style.transform = `${originalTransform} translateY(${delta}px)`
      }

      // 드래그 종료
      const handleDragEnd = () => {
        if (!isDragging) return

        // 애니메이션 복원
        drawerContent.style.transition = 'transform 250ms ease'

        // 150px 이상 드래그하면 닫기
        if (currentY > 150) {
          drawerContent.style.transform = `${originalTransform} translateY(100%)`

          // 애니메이션 완료 후 닫기
          setTimeout(() => {
            onClose()
          }, 250)
        } else {
          // 원래 위치로 돌아가기
          drawerContent.style.transform = originalTransform
        }

        isDragging = false
        console.log(`Drawer ${drawerId} 드래그 종료`)
      }

      // 터치 이벤트 핸들러
      const handleTouchStart = (e: TouchEvent) => {
        e.stopPropagation() // 이벤트 전파 중지
        handleDragStart(e.touches[0].clientY)
      }

      const handleTouchMove = (e: TouchEvent) => {
        e.stopPropagation() // 이벤트 전파 중지
        handleDrag(e.touches[0].clientY)
      }

      const handleTouchEnd = (e: TouchEvent) => {
        e.stopPropagation() // 이벤트 전파 중지
        handleDragEnd()
      }

      // 마우스 이벤트 핸들러
      const handleMouseDown = (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation() // 이벤트 전파 중지
        handleDragStart(e.clientY)

        // 마우스 이벤트 핸들러에 Drawer ID 바인딩
        const handleMouseMoveWithId = (e: MouseEvent) => {
          e.stopPropagation() // 이벤트 전파 중지
          handleDrag(e.clientY)
        }

        const handleMouseUpWithId = (e: MouseEvent) => {
          e.stopPropagation() // 이벤트 전파 중지
          handleDragEnd()
          // 전역 이벤트 리스너 제거
          document.removeEventListener('mousemove', handleMouseMoveWithId)
          document.removeEventListener('mouseup', handleMouseUpWithId)
        }

        // 전역 이벤트 리스너 (현재 드래그에 바인딩)
        document.addEventListener('mousemove', handleMouseMoveWithId)
        document.addEventListener('mouseup', handleMouseUpWithId)
      }

      const handle = handleRef.current

      // 이벤트 리스너 등록 - passive를 false로 설정하여 preventDefault 허용
      handle.addEventListener('touchstart', handleTouchStart, { passive: false })
      handle.addEventListener('touchmove', handleTouchMove, { passive: false })
      handle.addEventListener('touchend', handleTouchEnd)
      handle.addEventListener('mousedown', handleMouseDown)

      // 컴포넌트 언마운트 또는 drawer 닫힐 때 이벤트 리스너 제거
      return () => {
        handle.removeEventListener('touchstart', handleTouchStart)
        handle.removeEventListener('touchmove', handleTouchMove)
        handle.removeEventListener('touchend', handleTouchEnd)
        handle.removeEventListener('mousedown', handleMouseDown)
      }
    }, 300) // 300ms 지연 (Drawer가 완전히 마운트된 후)

    return () => clearTimeout(timeoutId)
  }, [opened, onClose, drawerId])

  // MutationObserver로 z-index 조정
  useEffect(() => {
    if (!opened) return

    // 다른 Drawer 위에 표시되도록 z-index 조정
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && drawerElementRef.current) {
          // 모든 Drawer 가져오기
          const allDrawers = document.querySelectorAll('.mantine-Drawer-content')
          if (allDrawers.length > 1) {
            // 마지막 Drawer가 최상단에 오도록 z-index 조정
            let maxZIndex = 1000
            allDrawers.forEach(drawer => {
              const zIndex = parseInt(window.getComputedStyle(drawer).zIndex || '0')
              maxZIndex = Math.max(maxZIndex, zIndex)
            })

            // 현재 Drawer z-index 설정
            if (drawerElementRef.current === allDrawers[allDrawers.length - 1]) {
              drawerElementRef.current.style.zIndex = (maxZIndex + 10).toString()
            }
          }
        }
      })
    })

    // body에 새 Drawer가 추가되는지 관찰
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [opened])

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      withOverlay={true}
      size={height ? height : `calc(100% - 72px)`}
      withCloseButton={false}
      id={`mantine-drawer-${drawerId}`} // 고유 ID 추가
      styles={{
        header: {
          height: '28px',
          minHeight: '28px',
        },
        content: {
          borderTopRightRadius: '16px',
          borderTopLeftRadius: '16px',
          padding: 0,
          overflow: 'hidden',
        },
        body: {
          padding: 0,
          height: '100%',
          overflow: 'hidden',
        },
      }}
    >
      {/* 상단 핸들 */}
      <div
        className="sticky left-0 top-0 z-50 bg-white"
        ref={handleRef}
        data-drawer-handle={drawerId} // 고유 식별자 추가
      >
        <div
          className="flex h-7 w-full cursor-pointer items-center justify-center"
          onClick={e => {
            e.stopPropagation() // 이벤트 전파 방지
            onClose()
          }}
        >
          <p className="h-1 w-12 bg-gray-200"></p>
        </div>
      </div>
      <div
        className="relative h-[calc(100%-28px)] w-full overflow-auto"
        id={`pre-notice-drawer`} // 고유 ID 사용
        ref={contentRef}
      >
        {children}
      </div>
    </Drawer>
  )
}

export default PreNoticeDrawer
